<template>
  <tr :class="{ 'bg-green-50': isSelected }">
    <!-- Product Details -->
    <td class="p-3">
      <div class="flex items-start gap-3">
        <!-- Product thumbnail, links to the product detail page (feedback #05) -->
        <Link :href="route('products.show', { sku: product.sku })" class="flex-shrink-0">
          <img
            :src="thumbnailUrl"
            :alt="localizedName"
            class="w-12 h-12 object-contain rounded bg-white border border-gray-200"
            loading="lazy"
            decoding="async"
            @error="handleImageError"
          />
        </Link>

        <div class="text-wrap flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <!-- Green checkmark for selected items. Kept in the layout (invisible) when not
                 selected so the product name does not shift on quantity changes (feedback #09). -->
            <svg
              :class="['w-4 h-4 text-green-600 flex-shrink-0', isSelected ? 'visible' : 'invisible']"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>

            <div class="flex-1">
              <!-- Product name links to the internal product detail page (feedback #05) -->
              <Link :href="route('products.show', { sku: product.sku })" class="link-primary">
                <h6 :class="['mb-1', isSelected ? 'font-bold' : 'font-semibold']" v-html="localizedName"></h6>
              </Link>
            </div>
          </div>

          <p :class="['text-xs', isSelected ? 'text-gray-600 font-medium' : 'text-gray-400']">
            SKU: {{ product.sku.replace('-de', '') }}
            <span class="inline-flex items-center ml-1">
              <svg class="w-2.5 h-2.5 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
              {{ getCartonQuantity(product.cartonQuantity) }}/{{ $t('Karton') }}
            </span>
            <span class="inline-flex items-center ml-1.5">
              <span :class="statusColor" class="w-1.5 h-1.5 rounded-full mr-0.5"></span>
              <template v-if="isSoldOut">
                <span class="text-red-600 font-medium">{{ $t('Ausverkauft') }}</span>
              </template>
              <template v-else-if="isInStock">
                {{ $t('Sofort verfügbar') }}<template v-if="hasAvailableQuantity"> ({{ availableQuantity }})</template>
              </template>
              <template v-else-if="isPreOrder">
                <span class="text-red-800 dark:text-red-700">
                  {{ $t('Vorbestellung') }} - {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
                </span>
              </template>
              <template v-else>
                {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
              </template>
            </span>
          </p>

          <!-- Quantity split hint: when the chosen amount exceeds what can ship immediately, the
               backend ships the available units now and back-orders the rest as a pre-order. -->
          <p
            v-if="availabilitySplit.isSplit"
            class="mt-1 text-xs font-medium text-amber-700 inline-flex items-center gap-1"
          >
            <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {{ $t(':available sofort lieferbar, :rest als Vorbestellung', { available: availabilitySplit.available, rest: availabilitySplit.rest }) }}
          </p>
        </div>
      </div>
    </td>

    <!-- Per-tier prices, or a single fixed price when identical across all tiers (feedback #07d) -->
    <template v-if="!isFixedPriceProduct">
      <!-- Tier 1 Price -->
      <td class="text-center p-3 text-sm">
        <div v-if="isActiveTier(1)" class="inline-block bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
          <div :class="['text-xs', isSelected ? 'font-bold' : 'font-medium']">{{ $t('Stufe') }} 1</div>
          <div :class="['font-semibold', isSelected ? 'font-bold' : '']">{{ formatPrice(getTierPrice(1)) }}</div>
        </div>
        <div v-else :class="[isSelected ? 'text-gray-800 font-semibold' : 'text-gray-600']">
          <!-- Tier label shown above inactive tiers too, so the numbers have context (feedback #06) -->
          <div class="text-xs font-medium text-gray-400">{{ $t('Stufe') }} 1</div>
          {{ formatPrice(getTierPrice(1)) }}
        </div>
      </td>

      <!-- Tier 2 Price -->
      <td class="text-center p-3 text-sm">
        <div v-if="isActiveTier(2)" class="inline-block bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
          <div :class="['text-xs', isSelected ? 'font-bold' : 'font-medium']">{{ $t('Stufe') }} 2</div>
          <div :class="['font-semibold', isSelected ? 'font-bold' : '']">{{ formatPrice(getTierPrice(2)) }}</div>
        </div>
        <div v-else :class="[isSelected ? 'text-gray-800 font-semibold' : 'text-gray-600']">
          <div class="text-xs font-medium text-gray-400">{{ $t('Stufe') }} 2</div>
          {{ formatPrice(getTierPrice(2)) }}
        </div>
      </td>

      <!-- Tier 3 Price -->
      <td class="text-center p-3 text-sm">
        <div v-if="isActiveTier(3)" class="inline-block bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
          <div :class="['text-xs', isSelected ? 'font-bold' : 'font-medium']">{{ $t('Stufe') }} 3</div>
          <div :class="['font-semibold', isSelected ? 'font-bold' : '']">{{ formatPrice(getTierPrice(3)) }}</div>
        </div>
        <div v-else :class="[isSelected ? 'text-gray-800 font-semibold' : 'text-gray-600']">
          <div class="text-xs font-medium text-gray-400">{{ $t('Stufe') }} 3</div>
          {{ formatPrice(getTierPrice(3)) }}
        </div>
      </td>
    </template>

    <!-- Fixed price: single value spanning the three tier columns (feedback #07d) -->
    <td v-else colspan="3" class="text-center p-3 text-sm">
      <div :class="['inline-block px-3 py-2', isSelected ? 'text-gray-900 font-bold' : 'text-gray-700 font-semibold']">
        <div class="text-xs font-medium text-gray-400">{{ $t('Fixpreis') }}</div>
        {{ formatPrice(getNetDiscountedPrice()) }}
      </div>
    </td>

    <!-- Quantity Selector -->
    <td class="p-3 text-center">
      <div class="flex items-center justify-center gap-2">
        <div class="flex items-center justify-center">
          <button
            @click="decrementQuantity"
            :disabled="!canDecrement"
            class="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-l-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            type="button"
          >
            <span class="text-gray-600 font-medium text-lg leading-none">−</span>
          </button>

          <div class="relative" ref="dropdownRef">
            <button
              @click="toggleDropdown"
              class="w-12 h-8 flex items-center justify-center border-t border-b border-gray-300 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
              type="button"
            >
              <span class="text-sm font-medium text-gray-800">{{ localQuantity }}</span>
              <span v-if="isUpdating" class="absolute -top-1 -right-1 w-2 h-2 bg-sonnenglas-500 rounded-full animate-pulse"></span>
            </button>

            <!-- Dropdown -->
            <div
              v-if="showDropdown"
              class="absolute top-full left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-64 overflow-y-auto min-w-[80px]"
            >
              <button
                v-for="qty in quantityOptions"
                :key="qty"
                @click="selectQuantity(qty)"
                class="w-full px-3 py-2 text-sm text-center hover:bg-gray-100 transition-colors whitespace-nowrap"
                :class="{ 'bg-sonnenglas-50 text-sonnenglas-600': qty === localQuantity }"
                type="button"
              >
                {{ qty }}
              </button>
            </div>
          </div>

          <button
            @click="incrementQuantity"
            :disabled="!canIncrement"
            class="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-r-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            type="button"
          >
            <span class="text-gray-600 font-medium text-lg leading-none">+</span>
          </button>
        </div>

        <!-- Remove item button: one-click delete from cart. Kept in the layout (invisible) when not
             selected so the quantity stepper does not shift on quantity changes (feedback #09). -->
        <button
          :class="['w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors', isSelected ? 'visible' : 'invisible']"
          :disabled="!isSelected"
          @click="removeFromCart"
          type="button"
          :title="$t('Artikel entfernen')"
          :aria-label="$t('Artikel entfernen')"
        >
          <!-- Inline trash icon (matches existing inline-SVG style used elsewhere in the cart) -->
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
          </svg>
        </button>
      </div>

      <!-- Split hint right at the quantity selector, so the partner immediately sees that the chosen
           amount exceeds what ships now. Updates reactively on every qty change (no API call). -->
      <p v-if="availabilitySplit.isSplit" class="mt-1.5 text-xs font-medium text-amber-700 leading-tight">
        {{ $t(':available von :chosen sofort verfügbar', { available: availabilitySplit.available, chosen: localQuantity }) }}
      </p>

      <!-- Cap note: the chosen quantity has reached the hard orderable limit (immediate available +
           usable replenishment). Only shown when a finite cap applies (never for POS or unknown). -->
      <p v-if="isCappedByStock" class="mt-1.5 text-xs font-medium text-amber-700 leading-tight">
        {{ $t(':max maximal bestellbar (inkl. Vorbestellung)', { max: product.maxOrderableQuantity }) }}
      </p>

      <!-- Hidden input to maintain form compatibility -->
      <input
        type="hidden"
        :name="`quantities[${product.sku}]`"
        :value="localQuantity"
      >
    </td>

    <!-- Subtotal: left blank at quantity 0 to reduce visual noise (feedback #08) -->
    <td :class="['text-right p-3', isSelected ? 'font-bold text-gray-900' : '']">
      <span v-if="isSelected">{{ formatPrice(getNetDiscountedPrice() * localQuantity) }}</span>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted, inject } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useQuantityOptions } from '@/composables/useQuantityOptions'
import { useDeliveryStatus } from '@/composables/useDeliveryStatus'
import { getCartonQuantity } from '@/utils/cartonQuantities'
import { getProductName } from '@/utils/localization'

interface Product {
  sku: string
  name: string | Record<string, string>
  productUrl?: string
  description: string | Record<string, string> | null
  image?: string
  images?: { main?: string; thumbnail?: string; gallery?: string[] }
  price?: { value: number; currency: string }
  fixedNetPrice?: { value: number; currency: string }
  fixedNetPriceLaunch?: { value: number; currency: string }
  excludedFromDiscounts?: boolean
  deliveryInWeeksFrom?: number
  deliveryInWeeksTo?: number
  availableQuantities: number[]
  availableQuantity?: number | null
  maxOrderableQuantity?: number | null
  soldOut?: boolean
  isPos?: boolean
  cartonQuantity?: number
  preOrder?: boolean
  discontinued?: boolean
}

interface Props {
  product: Product
  quantity: number
  currency: string
  pricingTiers: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto[]
  productTierPricing: Record<string, Record<string, number>>
  currentPricingTier: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto
  taxRate: number
}

interface Emits {
  (e: 'update-quantity', sku: string, quantity: number): void
  (e: 'show-pricing-modal'): void
  (e: 'show-fixed-price-modal'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Ziggy route helper (provided globally), used for the product detail link (feedback #05)
const route = inject('route') as any

const localizedName = computed(() => {
  return getProductName(props.product)
})

// Thumbnail for the cart row: prefer the Xhosa API image, fall back to the local
// product image so customers can recognise the product without leaving the cart (feedback #05).
const thumbnailUrl = computed((): string => {
  const p = props.product
  if (p.images?.thumbnail) return p.images.thumbnail
  if (p.images?.main) return p.images.main
  if (p.image) return p.image
  const baseSku = p.sku.toLowerCase().split('+')[0].split('-').slice(0, 2).join('-')
  return `/images/products/${baseSku}_thumb.jpg`
})

// A product has a fixed price when it is identical across all tiers; in that case the
// per-tier columns are hidden and a single price is shown instead (feedback #07d).
const isFixedPriceProduct = computed((): boolean => {
  const p = props.product
  // POS materials are free and have a single price, so no tiers are shown (feedback #07c).
  if (p.isPos || p.excludedFromDiscounts || p.fixedNetPrice || p.fixedNetPriceLaunch) {
    return true
  }
  const pricing = props.productTierPricing[p.sku]
  if (!pricing) return false
  const prices = [pricing.tier1, pricing.tier2, pricing.tier3].filter(
    (v) => v !== undefined && v !== null && !isNaN(v as number)
  )
  return prices.length > 1 && prices.every((v) => v === prices[0])
})

// Use delivery status composable for consistent behavior across all components
const { isInStock, isSoldOut, deliveryWeeks, statusColor, isPreOrder, isMonths, availableQuantity, hasAvailableQuantity, splitFor } = useDeliveryStatus(computed(() => props.product))

const formatPrice = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: props.currency || 'EUR'
  }).format(amount)
}

const getTierPrice = (tierNumber: number): number => {
  const productPricing = props.productTierPricing[props.product.sku]
  if (!productPricing) {
    return props.product.price?.value || 0
  }
  const tierPrice = productPricing[`tier${tierNumber}`]
  if (tierPrice === undefined || tierPrice === null || isNaN(tierPrice)) {
    return props.product.price?.value || 0
  }
  return tierPrice
}

const handleQuantityChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const newQuantity = parseInt(target.value, 10)
  emit('update-quantity', props.product.sku, newQuantity)
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  if (target.src.includes('_thumb.jpg')) {
    target.src = target.src.replace('_thumb.jpg', '.jpg')
    return
  }
  if (!target.src.includes('placeholder')) {
    target.src = '/images/products/placeholder.jpg'
  }
}

const getNetDiscountedPrice = (): number => {
  if (props.product.excludedFromDiscounts) {
    if (props.product.fixedNetPrice) {
      return props.product.fixedNetPrice.value
    }
    return props.product.price?.value || 0
  }

  if (props.product.fixedNetPriceLaunch) {
    return props.product.fixedNetPriceLaunch.value
  }

  if (props.product.fixedNetPrice) {
    return props.product.fixedNetPrice.value
  }

  // Use tier-based pricing from productTierPricing
  const productPricing = props.productTierPricing[props.product.sku]
  if (productPricing) {
    const tierKey = `tier${props.currentPricingTier.id}`
    const tierPrice = productPricing[tierKey]
    if (tierPrice !== undefined && tierPrice !== null && !isNaN(tierPrice)) {
      return tierPrice
    }
  }

  // Fallback to original price
  return props.product.price?.value || 0
}

const getFixedPriceDiscount = (): number => {
  if (!props.product.fixedNetPrice || !props.product.price) return 0
  
  const grossPrice = props.product.price.value
  const netPrice = props.product.fixedNetPrice.value
  return Math.round((1 - netPrice / grossPrice) * 100)
}

const showPricingTiersModal = () => {
  emit('show-pricing-modal')
}

const showFixedPriceModal = () => {
  emit('show-fixed-price-modal')
}

const isActiveTier = (tierNumber: number): boolean => {
  return props.currentPricingTier?.id === tierNumber
}

const isSelected = computed(() => {
  return localQuantity.value > 0
})

const localQuantity = ref(props.quantity)
const isUpdating = ref(false)
const showDropdown = ref(false)

// Split the chosen line quantity into the part that ships immediately and the part that becomes a
// pre-order, mirroring the backend which ships min(available, chosen) now and back-orders the rest.
const availabilitySplit = computed(() => splitFor(localQuantity.value))

// Use the composable to get carton-based quantity options. POS materials are capped at 1; non-POS
// options are capped at the hard orderable limit (immediate available + usable replenishment).
const { quantityOptions } = useQuantityOptions(props.product.cartonQuantity ?? 1, props.product.isPos, props.product.maxOrderableQuantity)

// Effective per-SKU maximum: POS is capped at 1, non-POS at the hard orderable limit when the API
// provides one, otherwise the default 1000. Drives the stepper's canIncrement guard.
const effectiveMaxQuantity = computed((): number => {
  if (props.product.isPos) {
    return 1
  }

  const max = props.product.maxOrderableQuantity

  return typeof max === 'number' && Number.isFinite(max) && max >= 0 ? Math.floor(max) : 1000
})

// True once the chosen quantity has reached a finite stock+replenishment cap, so a short note can
// explain why it cannot be increased. Never shown for POS or when the cap is unknown (null).
const isCappedByStock = computed((): boolean => {
  if (props.product.isPos) {
    return false
  }

  const max = props.product.maxOrderableQuantity

  return typeof max === 'number' && Number.isFinite(max) && localQuantity.value >= max
})

// Update local quantity when props change
watch(() => props.quantity, (newQuantity) => {
  localQuantity.value = newQuantity
})

const incrementQuantity = (): void => {
  if (canIncrement.value) {
    // Find the next quantity option
    const options = quantityOptions.value
    const currentIndex = options.indexOf(localQuantity.value)
    
    if (currentIndex !== -1 && currentIndex < options.length - 1) {
      const newQuantity = options[currentIndex + 1]
      localQuantity.value = newQuantity
      updateQuantityWithDebounce(newQuantity)
    } else if (currentIndex === -1 && localQuantity.value < 1000) {
      // If current value is not in options, find the next higher option
      const nextOption = options.find(opt => opt > localQuantity.value)
      if (nextOption) {
        localQuantity.value = nextOption
        updateQuantityWithDebounce(nextOption)
      }
    }
  }
}

const decrementQuantity = (): void => {
  if (canDecrement.value) {
    // Find the previous quantity option
    const options = quantityOptions.value
    const currentIndex = options.indexOf(localQuantity.value)

    if (currentIndex > 0) {
      const newQuantity = options[currentIndex - 1]
      localQuantity.value = newQuantity
      updateQuantityWithDebounce(newQuantity)
    } else if (currentIndex === -1 && localQuantity.value > 0) {
      // If current value is not in options, find the next lower option
      const prevOption = options.reverse().find(opt => opt < localQuantity.value)
      if (prevOption) {
        localQuantity.value = prevOption
        updateQuantityWithDebounce(prevOption)
      }
    }
  }
}

// Remove this product completely from the cart in a single click.
// Re-uses the existing update-quantity emit (parent routes it through the cart store
// / Inertia POST), the same path that the qty stepper would take when reaching zero.
const removeFromCart = (): void => {
  if (localQuantity.value === 0) return

  // Cancel any pending debounced update so we don't race with a stale qty.
  if (updateTimeout) {
    clearTimeout(updateTimeout)
    updateTimeout = null
  }

  localQuantity.value = 0
  showDropdown.value = false
  emit('update-quantity', props.product.sku, 0)
}

let updateTimeout: number | null = null
const updateQuantityWithDebounce = (quantity: number): void => {
  isUpdating.value = true
  
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
  
  updateTimeout = window.setTimeout(() => {
    emit('update-quantity', props.product.sku, quantity)
    isUpdating.value = false
  }, 500) // 500ms debounce
}

const selectQuantity = (quantity: number): void => {
  localQuantity.value = quantity
  showDropdown.value = false
  // For dropdown selections, update immediately without debounce to ensure tier highlighting updates
  isUpdating.value = true
  
  // Clear any pending debounced update
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
  
  // Emit immediately for dropdown selections to ensure proper tier calculation
  emit('update-quantity', props.product.sku, quantity)
  
  // Set a short timeout to reset updating state after the parent has processed
  setTimeout(() => {
    isUpdating.value = false
  }, 100)
}

const toggleDropdown = (): void => {
  showDropdown.value = !showDropdown.value
}

const canIncrement = computed((): boolean => {
  return localQuantity.value < effectiveMaxQuantity.value
})

const canDecrement = computed((): boolean => {
  return localQuantity.value > 0
})

// Close dropdown when clicking outside
const dropdownRef = ref<HTMLElement | null>(null)

const handleClickOutside = (event: Event): void => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
})
</script>

<style scoped>
.badge.pricing-tier:hover {
  @apply shadow-md;
}

.delivery-available {
  @apply text-green-600;
}

.delivery-indelivery {
  @apply text-yellow-600;
}

.delivery-backordered {
  @apply text-red-600;
}
</style>